/**
 * MiniMonth (calendar navigation, DESIGN.md T13): month header with previous/next, weekday initials and the days.
 * One tab stop; arrow keys move the focus by day/week, Page Up/Down by month, Enter selects (roving tabindex).
 */
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Button } from '../components/button';
import { cn } from '../lib/utils';
import { addDays, addMonths, monthMatrix, sameDay } from './model';

export interface MiniMonthProps {
  selected: Date;
  /** A reference day can be provided without presenting it as a chosen value. */
  showSelection?: boolean;
  min?: Date;
  max?: Date;
  disabled?: boolean;
  /** Optional week-number column, using the caller's regional convention and translations. */
  weekNumbers?: { heading: string; label: (weekStart: Date) => string; accessibleLabel: (weekStart: Date) => string };
  /** Days highlighted as part of the shown range (week view). */
  rangeStart?: Date | undefined;
  rangeEnd?: Date | undefined;
  now: Date;
  weekStartsOn: number;
  onSelect: (day: Date) => void;
  formatMonth: (month: Date) => string;
  formatWeekdayShort: (day: Date) => string;
  formatDayNumber: (day: Date) => string;
  formatDayLabel: (day: Date) => string;
  labels: { previousMonth: string; nextMonth: string };
  className?: string;
}

export function MiniMonth({ selected, showSelection = true, min, max, disabled = false, weekNumbers, rangeStart, rangeEnd, now, weekStartsOn, onSelect, formatMonth, formatWeekdayShort, formatDayNumber, formatDayLabel, labels, className }: MiniMonthProps) {
  const [shown, setShown] = useState(() => new Date(selected.getFullYear(), selected.getMonth(), 1));
  const [focus, setFocus] = useState(selected);
  const grid = useRef<HTMLDivElement>(null);
  const focusing = useRef(false);
  const weeks = useMemo(() => monthMatrix(shown, weekStartsOn), [shown, weekStartsOn]);

  // Follow the selection from outside (command bar navigation): adjust state while rendering.
  const [followed, setFollowed] = useState(selected.getTime());
  if (followed !== selected.getTime()) {
    setFollowed(selected.getTime());
    setShown(new Date(selected.getFullYear(), selected.getMonth(), 1));
    setFocus(selected);
  }

  useEffect(() => {
    if (!focusing.current) return;
    focusing.current = false;
    grid.current?.querySelector<HTMLButtonElement>('[tabindex="0"]')?.focus();
  }, [focus]);

  const move = (next: Date) => {
    if (disabled) return;
    if (min && next < min) next = min;
    if (max && next > max) next = max;
    focusing.current = true;
    setFocus(next);
    if (next.getMonth() !== shown.getMonth() || next.getFullYear() !== shown.getFullYear()) setShown(new Date(next.getFullYear(), next.getMonth(), 1));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const steps: Record<string, () => Date> = {
      ArrowLeft: () => addDays(focus, -1),
      ArrowRight: () => addDays(focus, 1),
      ArrowUp: () => addDays(focus, -7),
      ArrowDown: () => addDays(focus, 7),
      PageUp: () => addMonths(focus, -1),
      PageDown: () => addMonths(focus, 1),
      Home: () => addDays(focus, -((focus.getDay() - weekStartsOn + 7) % 7)),
      End: () => addDays(focus, 6 - ((focus.getDay() - weekStartsOn + 7) % 7)),
    };
    const step = steps[event.key];
    if (!step) return;
    event.preventDefault();
    move(step());
  };

  const inRange = (day: Date) => rangeStart !== undefined && rangeEnd !== undefined && day >= rangeStart && day < rangeEnd;
  const available = (day: Date) => !disabled && !(min && day < min) && !(max && day > max);
  const focusInView = weeks.flat().some((day) => sameDay(day, focus) && available(day));
  const firstAvailable = weeks.flat().find(available);
  const columns = weekNumbers ? 'grid-cols-[1.75rem_repeat(7,minmax(0,1fr))]' : 'grid-cols-7';

  return (
    <div className={cn('flex flex-col gap-1 text-xs', className)}>
      <div className="flex items-center justify-between">
        <span className="px-1 text-sm font-medium" aria-live="polite">
          {formatMonth(shown)}
        </span>
        <div className="flex">
          <Button type="button" disabled={disabled || !!(min && new Date(shown.getFullYear(), shown.getMonth(), 0) < min)} variant="ghost" size="icon-sm" aria-label={labels.previousMonth} onClick={() => setShown(addMonths(shown, -1))}>
            <ChevronLeftIcon />
          </Button>
          <Button type="button" disabled={disabled || !!(max && new Date(shown.getFullYear(), shown.getMonth() + 1, 1) > max)} variant="ghost" size="icon-sm" aria-label={labels.nextMonth} onClick={() => setShown(addMonths(shown, 1))}>
            <ChevronRightIcon />
          </Button>
        </div>
      </div>
      <div role="grid" aria-label={formatMonth(shown)} ref={grid} onKeyDown={onKeyDown} className="flex flex-col">
        <div role="row" className={cn('grid', columns)}>
          {weekNumbers && <span role="columnheader" className="py-0.5 text-center text-muted-foreground">{weekNumbers.heading}</span>}
          {weeks[0]?.map((day) => (
            <span key={day.getDay()} role="columnheader" className="py-0.5 text-center text-muted-foreground">
              {formatWeekdayShort(day)}
            </span>
          ))}
        </div>
        {weeks.map((week) => (
          <div role="row" key={week[0]?.getTime()} className={cn('grid', columns)}>
            {weekNumbers && week[0] && <span role="rowheader" aria-label={weekNumbers.accessibleLabel(week[0])} className="flex items-center justify-center border-r text-muted-foreground">{weekNumbers.label(week[0])}</span>}
            {week.map((day) => {
              const isFocus = focusInView ? sameDay(day, focus) : !!firstAvailable && sameDay(day, firstAvailable);
              return (
                <span role="gridcell" key={day.getTime()} aria-selected={showSelection && sameDay(day, selected)}>
                  <button
                    type="button"
                    disabled={disabled || !!(min && day < min) || !!(max && day > max)}
                    tabIndex={isFocus ? 0 : -1}
                    aria-label={formatDayLabel(day)}
                    aria-current={sameDay(day, now) ? 'date' : undefined}
                    onFocus={() => setFocus(day)}
                    onClick={() => {
                      setFocus(day);
                      onSelect(day);
                    }}
                    className={cn(
                      'flex h-8 w-full items-center justify-center rounded-md outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 disabled:pointer-events-none',
                      day.getMonth() !== shown.getMonth() && 'text-muted-foreground',
                      inRange(day) && 'bg-accent',
                      sameDay(day, now) && 'font-semibold text-primary',
                      showSelection && sameDay(day, selected) && 'bg-primary text-primary-foreground hover:bg-primary',
                    )}
                  >
                    {formatDayNumber(day)}
                  </button>
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
